import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { EASE_EDITORIAL } from "./Reveal";

const LINKS = [
  { label: "Work", target: "work" },
  { label: "About", target: "about" },
  { label: "We Cook Art", target: "collective" },
  { label: "Contact", target: "contact" },
];

const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (target) => {
    setOpen(false);
    if (pathname !== "/") {
      navigate("/", { state: { scrollTo: target } });
      return;
    }
    const el = document.getElementById(target);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 z-50 w-full transition-all duration-700 ${
          scrolled || open ? "bg-ivory/85 backdrop-blur-md py-4" : "bg-transparent py-6"
        }`}
      >
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-6 sm:px-10">
          <Link
            to="/"
            data-testid="nav-home-link"
            onClick={() => setOpen(false)}
            className="font-serif text-2xl italic leading-none text-ink transition-colors hover:text-oxblood"
          >
            We&nbsp;Cook&nbsp;Art
          </Link>

          <nav className="hidden items-center gap-10 md:flex">
            {LINKS.map((l) => (
              <button
                key={l.target}
                data-testid={`nav-${l.target}-link`}
                onClick={() => goTo(l.target)}
                className="text-[0.7rem] uppercase tracking-[0.22em] text-ink-soft transition-colors hover:text-oxblood"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="flex flex-col items-end gap-[5px] md:hidden"
            aria-label="Menu"
          >
            <span className={`h-px bg-ink transition-all duration-500 ${open ? "w-6 translate-y-[6px] rotate-45" : "w-6"}`} />
            <span className={`h-px bg-ink transition-all duration-500 ${open ? "w-6 -translate-y-[6px] -rotate-45" : "w-4"}`} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.6, ease: EASE_EDITORIAL }}
            className="fixed inset-0 z-40 flex flex-col justify-center bg-ivory px-8 md:hidden"
            data-testid="mobile-menu"
          >
            {LINKS.map((l, i) => (
              <motion.button
                key={l.target}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 + i * 0.08, duration: 0.8, ease: EASE_EDITORIAL }}
                data-testid={`mobile-nav-${l.target}-link`}
                onClick={() => goTo(l.target)}
                className="border-b border-line py-6 text-left font-serif text-4xl text-ink"
              >
                {l.label}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
